// ─────────────────────────────────────────────────────────────────────────────
// Convention des quotas — appareils, chaînes, disque
//
// **-1 signifie « sans limite », et rien d'autre.**
//
// `0` vaut donc « aucun », et c'est la valeur par défaut : un compte qui n'a
// encore rien acheté ne peut rien créer. C'était l'inverse avant le 16/08/2026,
// où `0` voulait dire illimité — un compte neuf pouvait alors tout créer, et
// aucun écran ne pouvait distinguer « pas de droit » de « droit infini ».
//
// -1 est un réglage exceptionnel, posé directement en base. L'interface
// l'affiche « ∞ ».
//
// Doit rester le miroir exact de Admin::SANS_LIMITE côté admin-api.
// ─────────────────────────────────────────────────────────────────────────────

export const SANS_LIMITE = -1;

export const estSansLimite = (max: number): boolean => max === SANS_LIMITE;

/** Le plafond est-il atteint ? Toujours faux quand le quota est illimité. */
export const quotaAtteint = (utilise: number, max: number): boolean =>
  !estSansLimite(max) && utilise >= max;

/**
 * Octets disque encore disponibles. `Infinity` quand le quota est illimité :
 * `quotaDisk - quotaUsed` donnerait sinon un nombre NÉGATIF (-1 - utilisé), que
 * tout contrôle « taille > restant » prendrait pour « plus de place ».
 */
export const octetsRestants = (utilise: number, max: number): number =>
  estSansLimite(max) ? Infinity : Math.max(0, max - utilise);

/** Le plafond, tel qu'on le montre à l'opérateur. */
export const afficherLimite = (max: number): string =>
  estSansLimite(max) ? '∞' : String(max);

/**
 * Part restante d'un quota, entre 0 et 1 — pour les jauges.
 * Un quota illimité est toujours plein ; un quota nul est toujours vide.
 */
export const partRestante = (utilise: number, max: number): number => {
  if (estSansLimite(max)) { return 1; }
  if (max <= 0)           { return 0; }

  return Math.max(0, (max - utilise) / max);
};

// ─────────────────────────────────────────────────────────────────────────────
// Convention distincte des quotas de durée cumulée (diffusion / enregistrement) —
// durationMaxBroadcasting / durationMaxRecording sur Admin.
//
// Ces quotas se saisissent et s'affichent en HH:MM:SS (cf. AdminSheet), où -1
// n'a pas de sens. **99:99:99 signifie « sans limite »**, soit
// 99×3600 + 99×60 + 99 = 362439 secondes.
//
// Doit rester le miroir exact de Admin::DUREE_SANS_LIMITE côté admin-api.
// ─────────────────────────────────────────────────────────────────────────────

export const DUREE_SANS_LIMITE_SECONDES = 99 * 3600 + 99 * 60 + 99;

export const estDureeSansLimite = (secondes: number): boolean => secondes === DUREE_SANS_LIMITE_SECONDES;
